import { z } from 'zod';

export const StarTypeSchema = z.enum([
  'yellow_dwarf',
  'red_dwarf',
  'blue_giant',
  'white_dwarf',
  'neutron_star',
  'black_hole',
  'binary',
]);
export type StarType = z.infer<typeof StarTypeSchema>;

/**
 * Stellaris-style knowledge level of one star system for one civilization (server-authoritative).
 * The topology (every position and every lane) is public; everything else is earned:
 *  - unknown   anonymous silhouette: id, position only (no name, no star type, no owner)
 *  - detected  VISITED: a ship of the civilization has been there -> name, star type, last seen civilization
 *              (the legacy DB value is kept on purpose so existing knowledge survives the migration)
 *  - surveyed  a scout surveyed it -> details and deposits
 *  - colonized own system
 */
export const DiscoveryStatusSchema = z.enum([
  'unknown',
  'detected',
  'surveyed',
  'colonized',
]);
export type DiscoveryStatus = z.infer<typeof DiscoveryStatusSchema>;

// Amit a kliens kaphat – a fog of war már szerveren alkalmazva
export const StarSystemClientSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  x: z.number(),
  y: z.number(),
  starType: StarTypeSchema,
  discoveryStatus: DiscoveryStatusSchema,
  // részletek csak surveyed/colonized esetén
  details: z.object({
    habitability: z.number().min(0).max(100),
    resourceRichness: z.number().min(0).max(100),
    planetCount: z.number().int().min(0),
    description: z.string(),
  }).optional(),
  // ha a játékos civilizációja ott van
  isHomeSystem: z.boolean().optional(),
  // M3: ownership (null = semleges)
  owner: z.object({
    civilizationId: z.string().uuid(),
    civilizationName: z.string(),
    civilizationColor: z.string(),
    civilizationEmblem: z.string().optional(),
    isNpc: z.boolean().optional(),
  }).nullable().optional(),
  civilizationName: z.string().optional(), // backward compat
  /** Last sighting of the civilization living here (snapshot from the last visit/presence, see ownerSeenAt). */
  ownerSeenAt: z.string().datetime().optional(),
  ownerKind: z.enum(['owner', 'station', 'fleet']).optional(),
  /** Only for a sighting that is not ownership (a foreign fleet was present on arrival). */
  presentCiv: z.object({
    civilizationId: z.string().uuid(),
    civilizationName: z.string(),
    civilizationColor: z.string(),
  }).optional(),
});
export type StarSystemClient = z.infer<typeof StarSystemClientSchema>;

export const StarLaneClientSchema = z.object({
  id: z.string().uuid(),
  fromSystemId: z.string().uuid(),
  toSystemId: z.string().uuid(),
  /** Lane length in light years (the map unit). */
  distance: z.number().positive(),
});
export type StarLaneClient = z.infer<typeof StarLaneClientSchema>;
